import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import styled from "styled-components";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import { useClaimWorklist } from "./ClaimWorklistContext";
import { apiFetch } from "../lib/apiFetch";

const SLATE950 = "#121820";
const SLATE900 = "#1a212c";
const SLATE800 = "#232c39";
const SLATE700 = "#313d4d";
const SLATE600 = "#4a5768";
const SLATE500 = "#68778b";
const SLATE400 = "#9aa6b5";
const SLATE300 = "#d2d9e3";
const SLATE200 = "#e7ecf3";
const SLATE100 = "#f1f5f9";
const ACCENT = "#7a899c";

const SIDEBAR_COLLAPSED_STORAGE_KEY = "medcode:left-sidebar-collapsed";
interface UserProfile {
  firstName: string;
  lastName: string;
  role: string;
}

/* LAYOUT -------------------------------------------------- */

const LayoutWrapper = styled.div`
  display: flex;
  height: 100vh;
  background: ${SLATE950};
`;

const SideMenu = styled.div<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? "84px" : "296px")};
  min-width: ${({ $collapsed }) => ($collapsed ? "84px" : "296px")};
  background: ${SLATE900};
  border-right: 1px solid ${SLATE700};
  display: flex;
  flex-direction: column;
  height: 100%;
  transition: width 180ms ease-in-out;

  @media (max-width: 768px) {
    width: ${({ $collapsed }) => ($collapsed ? "68px" : "250px")};
    min-width: ${({ $collapsed }) => ($collapsed ? "68px" : "250px")};
  }
`;

const ScrollArea = styled.div<{ $collapsed: boolean }>`
  flex: 1;
  overflow-y: auto;
  margin: ${({ $collapsed }) => ($collapsed ? "0.55rem 0.38rem" : "0.75rem")};
  padding: ${({ $collapsed }) => ($collapsed ? "0.4rem 0.25rem" : "0.45rem 0.4rem")};
  background: ${SLATE800};
  border: 1px solid ${SLATE700};
  border-radius: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${SLATE500};
    border-radius: 3px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const BottomSection = styled.div<{ $collapsed: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? "0.7rem 0.5rem 0.9rem" : "0.8rem 1rem 1rem")};
  border-top: 1px solid ${SLATE700};
  display: flex;
  justify-content: center;
`;

const ContentArea = styled.div`
  flex: 1;
  position: relative;
  background: #f8fafc;
  overflow-y: auto;
  padding: 0.875rem;
`;

/* PROFILE HEADER ----------------------------------------- */

const ProfileSection = styled.div<{ $collapsed: boolean }>`
  padding: ${({ $collapsed }) => ($collapsed ? "0.9rem 0.55rem 0.75rem" : "1rem 1rem 0.8rem")};
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  gap: ${({ $collapsed }) => ($collapsed ? "0" : "0.75rem")};
  border-bottom: 1px solid ${SLATE700};
  cursor: pointer;
  position: relative;
`;

const ProfileImage = styled.img<{ $collapsed: boolean }>`
  width: ${({ $collapsed }) => ($collapsed ? "36px" : "44px")};
  height: ${({ $collapsed }) => ($collapsed ? "36px" : "44px")};
  border-radius: 50%;
`;

const ProfileInfo = styled.div<{ $collapsed: boolean }>`
  display: ${({ $collapsed }) => ($collapsed ? "none" : "block")};
  min-width: 0;

  h4 {
    margin: 0;
    color: ${SLATE200};
    font-size: 0.95rem;
    font-weight: 500;
  }
  p {
    margin: 0;
    color: ${SLATE400};
    font-size: 0.78rem;
  }
`;

const DropdownMenu = styled.div<{ $collapsed: boolean }>`
  position: absolute;
  top: 62px;
  left: ${({ $collapsed }) => ($collapsed ? "0.5rem" : "1rem")};
  width: 178px;
  background: ${SLATE800};
  border: 1px solid ${SLATE600};
  border-radius: 8px;
  padding: 0.5rem 0;
  z-index: 300;
  box-shadow: 0 10px 24px rgba(2, 6, 23, 0.45);
`;

const SideMenuToggle = styled.button<{ $collapsed: boolean }>`
  position: absolute;
  top: 0.5rem;
  right: ${({ $collapsed }) => ($collapsed ? "0.4rem" : "0.6rem")};
  width: 1.45rem;
  height: 1.45rem;
  border: 1px solid ${SLATE600};
  border-radius: 6px;
  background: rgba(122, 137, 156, 0.12);
  color: ${SLATE300};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.74rem;
  line-height: 1;
  cursor: pointer;

  &:hover {
    background: rgba(148, 163, 184, 0.22);
    color: ${SLATE200};
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.25);
  }
`;

const DropdownItem = styled.div`
  padding: 0.62rem 0.9rem;
  color: ${SLATE200};
  font-size: 0.86rem;
  cursor: pointer;

  &:hover {
    background: ${SLATE700};
  }
`;

/* MENU ITEMS --------------------------------------------- */

const MenuLink = styled(Link)<{ $collapsed?: boolean }>`
  font-size: 0.9rem;
  color: ${SLATE300};
  padding: ${({ $collapsed }) => ($collapsed ? "0.52rem 0.35rem" : "0.55rem 0.7rem")};
  margin: 0.2rem 0.35rem;
  border-radius: 8px;
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  gap: 0.45rem;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;

  &:hover {
    color: ${SLATE200};
    background: rgba(148, 163, 184, 0.12);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.25);
  }
`;

const MenuButton = styled.button<{ $collapsed?: boolean }>`
  width: calc(100% - 0.7rem);
  border: none;
  background: transparent;
  font-size: 0.9rem;
  color: ${SLATE300};
  padding: ${({ $collapsed }) => ($collapsed ? "0.52rem 0.35rem" : "0.55rem 0.7rem")};
  margin: 0.2rem 0.35rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${({ $collapsed }) => ($collapsed ? "center" : "flex-start")};
  gap: 0.45rem;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;

  &:hover {
    color: ${SLATE200};
    background: rgba(148, 163, 184, 0.12);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.25);
  }
`;

const MenuItemIcon = styled.span`
  width: 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.86rem;
  line-height: 1;
  flex-shrink: 0;
`;

const ProviderBlock = styled.div`
  margin: 0.2rem 0.35rem 0;
`;

const ProviderTitle = styled.button<{ $active?: boolean }>`
  width: 100%;
  border: none;
  background: ${({ $active }) => ($active ? "rgba(122, 137, 156, 0.16)" : "transparent")};
  color: ${SLATE300};
  padding: 0.56rem 0.58rem;
  border-radius: 8px;
  box-shadow: ${({ $active }) => ($active ? `inset 3px 0 0 ${ACCENT}` : "none")};
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;

  .label {
    display: block;
    margin-bottom: 0.2rem;
    font-size: 0.62rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${({ $active }) => ($active ? SLATE400 : SLATE500)};
  }

  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.45rem;
  }

  .name {
    font-size: 0.84rem;
    color: ${({ $active }) => ($active ? SLATE100 : SLATE200)};
    font-weight: ${({ $active }) => ($active ? 600 : 500)};
    line-height: 1.35;
  }

  .chevron {
    font-size: 0.8rem;
    color: ${SLATE400};
    display: inline-flex;
    align-items: center;
    line-height: 1;
    opacity: 0.68;
    transition: opacity 150ms ease-in-out, color 150ms ease-in-out;
  }

  &:hover {
    color: ${SLATE200};
    background: rgba(148, 163, 184, 0.12);

    .chevron {
      opacity: 0.95;
      color: ${SLATE300};
    }
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.3);
  }
`;

const ClaimList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-top: 0.3rem;
`;

const ClaimCard = styled(Link)<{ $active?: boolean }>`
  display: block;
  padding: 0.95rem 0.7rem 0.96rem 0.8rem;
  border-radius: 8px;
  border-left: 3px solid ${(p) => (p.$active ? ACCENT : "transparent")};
  background: ${(p) => (p.$active ? "rgba(122, 137, 156, 0.18)" : "transparent")};
  color: ${SLATE200};
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out;

  &:hover {
    background: ${(p) => (p.$active ? "rgba(122, 137, 156, 0.2)" : "rgba(122, 137, 156, 0.13)")};
  }

  .date {
    font-size: 0.74rem;
    color: ${(p) => (p.$active ? SLATE300 : SLATE400)};
    letter-spacing: 0.01em;
  }
  .name {
    margin-top: 0.15rem;
    font-size: 0.92rem;
    color: ${(p) => (p.$active ? SLATE100 : SLATE200)};
    font-weight: ${(p) => (p.$active ? 600 : 500)};
    line-height: 1.35;
  }
  .meta {
    margin-top: 0.15rem;
    font-size: 0.76rem;
    color: ${(p) => (p.$active ? SLATE300 : SLATE400)};
    line-height: 1.3;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: inset 0 0 0 2px rgba(100, 116, 139, 0.35);
  }
`;

const DeptBlock = styled.div`
  margin: 0.1rem 0.35rem 0.2rem 1.2rem;
  border-left: 2px solid ${SLATE700};
  padding-left: 0.5rem;
`;

const DeptCard = styled(Link)<{ $active?: boolean }>`
  display: block;
  padding: 0.52rem 0.65rem;
  border-radius: 7px;
  border-left: 3px solid ${(p) => (p.$active ? ACCENT : "transparent")};
  background: ${(p) => (p.$active ? "rgba(122, 137, 156, 0.18)" : "transparent")};
  color: ${SLATE300};
  text-decoration: none;
  transition: background-color 120ms, border-color 120ms;
  margin-bottom: 0.1rem;

  &:hover {
    background: rgba(122, 137, 156, 0.13);
    color: ${SLATE100};
  }

  .dept-name {
    font-size: 0.84rem;
    font-weight: ${(p) => (p.$active ? 600 : 500)};
    color: ${(p) => (p.$active ? SLATE100 : SLATE200)};
    line-height: 1.3;
  }

  .dept-meta {
    font-size: 0.72rem;
    color: ${SLATE500};
    margin-top: 0.1rem;
  }
`;


const SearchWrapper = styled.div`
  position: relative;
  margin: 0.35rem 0.35rem 0.55rem;
`;

const SearchInput = styled.input`
  width: 100%;
  background: rgba(122, 137, 156, 0.12);
  border: 1px solid ${SLATE600};
  color: ${SLATE200};
  padding: 0.58rem 2rem 0.58rem 0.86rem;
  border-radius: 9px;
  font-size: 0.86rem;
  box-sizing: border-box;

  &::placeholder {
    color: ${SLATE500};
  }

  &:focus-visible {
    outline: 0;
    border-color: ${SLATE400};
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.28);
  }
`;

const ClearSearchButton = styled.button`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: ${SLATE500};
  cursor: pointer;
  font-size: 1rem;
  padding: 0;
  line-height: 1;

  &:hover {
    color: ${SLATE200};
  }
`;

const SortOptions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.74rem;
  color: ${SLATE400};
  margin: 0.35rem 0.45rem 0.45rem;
`;

const SortOption = styled.button`
  background: transparent;
  color: ${SLATE300};
  border: 1px solid ${SLATE600};
  padding: 0.18rem 0.45rem;
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.72rem;
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out;

  &:hover {
    background: rgba(148, 163, 184, 0.14);
    border-color: ${SLATE500};
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.25);
  }
`;

const Spinner = styled.div`
  width: 24px;
  height: 24px;
  border: 3px solid ${SLATE700};
  border-left-color: ${SLATE200};
  border-radius: 50%;
  animation: spin 1s infinite linear;
  margin: 1rem auto;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const SearchResultsBlock = styled.div`
  margin: 0.1rem 0.35rem 0;
`;

const SearchStatusText = styled.div`
  padding: 0.55rem 0.45rem;
  font-size: 0.78rem;
  color: ${SLATE400};
`;

const ContentLoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 50;
  background: rgba(248, 250, 252, 0.82);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const OverlayLoaderWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;
`;

const OverlaySpinner = styled(Spinner)`
  margin: 0;
  width: 28px;
  height: 28px;
`;

const OverlayLabel = styled.div`
  font-size: 0.82rem;
  color: #334155;
  font-weight: 500;
`;

const BottomLink = styled(Link)<{ $collapsed: boolean }>`
  display: block;
  width: 100%;
  text-align: center;
  padding: ${({ $collapsed }) => ($collapsed ? "0.52rem 0.25rem" : "0.52rem 0.7rem")};
  border-radius: 8px;
  color: ${SLATE400};
  font-size: ${({ $collapsed }) => ($collapsed ? "0.72rem" : "0.8rem")};
  font-weight: 500;
  white-space: nowrap;
  transition: background-color 150ms ease-in-out, color 150ms ease-in-out;

  &:hover {
    color: ${SLATE200};
    background: rgba(148, 163, 184, 0.11);
  }
`;

/* MAIN LAYOUT -------------------------------------------- */

const Layout: React.FC = () => {
  const location = useLocation();
  const { athenaProviders, athenaSelectedProvider, setAthenaSelectedProvider } = useClaimWorklist();

  const navigate = useNavigate();

  const [athenaWorklistOpen, setAthenaWorklistOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [isSideMenuCollapsed, setIsSideMenuCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY) === "1";
  });

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const fetchProfile = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const res = await apiFetch("/auth/profile");
        if (res.ok) {
          const data = await res.json();
          setProfile(data);
        } else {
          setProfile({ firstName: "User", lastName: "", role: "N/A" });
        }
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setProfile({ firstName: "User", lastName: "", role: "N/A" });
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/dashboard") {
      setDashboardLoading(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, isSideMenuCollapsed ? "1" : "0");
  }, [isSideMenuCollapsed]);

  useEffect(() => {
    if (!isSideMenuCollapsed) return;
    setAthenaWorklistOpen(false);
    setDropdownOpen(false);
  }, [isSideMenuCollapsed]);

  const isAdmin = profile?.role?.toLowerCase() === "admin";

  const handleLogout = async () => {
    await signOut(auth);
    window.location.href = "/login";
  };

  const toggleSideMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSideMenuCollapsed((prev) => !prev);
  };

  return (
    <LayoutWrapper>
      <SideMenu $collapsed={isSideMenuCollapsed}>
        <ProfileSection $collapsed={isSideMenuCollapsed} onClick={() => setDropdownOpen(!dropdownOpen)}>
          <ProfileImage $collapsed={isSideMenuCollapsed} src="/logo.png" loading="lazy" />
          <ProfileInfo $collapsed={isSideMenuCollapsed}>
            {profile ? (
              <>
                <h4>{`${profile.firstName} ${profile.lastName}`}</h4>
                <p>{profile.role}</p>
              </>
            ) : <p>Loading...</p>}
          </ProfileInfo>

          <SideMenuToggle
            type="button"
            $collapsed={isSideMenuCollapsed}
            onClick={toggleSideMenu}
            aria-label={isSideMenuCollapsed ? "Expand side menu" : "Collapse side menu"}
            title={isSideMenuCollapsed ? "Expand side menu" : "Collapse side menu"}
          >
            {isSideMenuCollapsed ? "▸" : "◂"}
          </SideMenuToggle>

          {dropdownOpen && (
            <DropdownMenu $collapsed={isSideMenuCollapsed} ref={dropdownRef}>
              <DropdownItem>My Profile</DropdownItem>
              <DropdownItem onClick={handleLogout}>Logout</DropdownItem>
            </DropdownMenu>
          )}
        </ProfileSection>

        <ScrollArea $collapsed={isSideMenuCollapsed}>
          <MenuLink
            $collapsed={isSideMenuCollapsed}
            to="/auto-claims"
            title="Auto Claims"
          >
            <MenuItemIcon>⚡</MenuItemIcon>
            {!isSideMenuCollapsed && "Auto Claims"}
          </MenuLink>

          {isAdmin && (
            <MenuLink
              $collapsed={isSideMenuCollapsed}
              to="/dashboard"
              title="Dashboard"
              onClick={() => {
                if (location.pathname !== "/dashboard") {
                  setDashboardLoading(true);
                }
              }}
            >
              <MenuItemIcon>⌂</MenuItemIcon>
              {!isSideMenuCollapsed && "Dashboard"}
            </MenuLink>
          )}

          <MenuButton
            type="button"
            $collapsed={isSideMenuCollapsed}
            title="Athena Worklist"
            onClick={() => {
              if (isSideMenuCollapsed) {
                setIsSideMenuCollapsed(false);
                setAthenaWorklistOpen(true);
                return;
              }
              setAthenaWorklistOpen((prev) => !prev);
            }}
          >
            <MenuItemIcon>⊞</MenuItemIcon>
            {!isSideMenuCollapsed && `Athena Worklist ${athenaWorklistOpen ? "▾" : "▸"}`}
          </MenuButton>

          {athenaWorklistOpen && !isSideMenuCollapsed && (
            <DeptBlock>
              {athenaProviders.length === 0 ? (
                <DeptCard to="/worklist" $active={location.pathname === "/worklist"}>
                  <div className="dept-name">Open Active Worklist</div>
                </DeptCard>
              ) : (
                <>
                  <DeptCard
                    to="/worklist"
                    $active={location.pathname === "/worklist" && athenaSelectedProvider === null}
                    onClick={() => setAthenaSelectedProvider(null)}
                  >
                    <div className="dept-name" style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>All Providers</span>
                      <span style={{ fontSize: "0.72rem", opacity: 0.65 }}>
                        {athenaProviders.reduce((s, p) => s + p.count, 0)}
                      </span>
                    </div>
                  </DeptCard>

                  {athenaProviders.map((p) => (
                    <DeptCard
                      key={p.name}
                      to="/worklist"
                      $active={location.pathname === "/worklist" && athenaSelectedProvider === p.name}
                      onClick={() => {
                        setAthenaSelectedProvider(p.name);
                        if (location.pathname !== "/worklist") navigate("/worklist");
                      }}
                    >
                      <div className="dept-name" style={{ display: "flex", justifyContent: "space-between" }}>
                        <span>{p.name}</span>
                        <span style={{ fontSize: "0.72rem", opacity: 0.65 }}>{p.count}</span>
                      </div>
                    </DeptCard>
                  ))}
                </>
              )}
            </DeptBlock>
          )}

          <MenuLink
            $collapsed={isSideMenuCollapsed}
            to="/encounter-search"
            title="Encounter Search"
          >
            <MenuItemIcon>⌕</MenuItemIcon>
            {!isSideMenuCollapsed && "Encounter Search"}
          </MenuLink>

        </ScrollArea>

        <BottomSection $collapsed={isSideMenuCollapsed}>
          <BottomLink $collapsed={isSideMenuCollapsed} to="/medcoder-ai" title="AI Assistant">
            {isSideMenuCollapsed ? "AI" : "AI Assistant"}
          </BottomLink>
        </BottomSection>
      </SideMenu>

      <ContentArea>
        {dashboardLoading && (
          <ContentLoaderOverlay role="status" aria-live="polite" aria-label="Loading dashboard">
            <OverlayLoaderWrap>
              <OverlaySpinner />
              <OverlayLabel>Loading dashboard data...</OverlayLabel>
            </OverlayLoaderWrap>
          </ContentLoaderOverlay>
        )}
        <Outlet context={{ setDashboardLoading, profile }} />
      </ContentArea>
    </LayoutWrapper>
  );
};

export default memo(Layout);
